import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  NuclearTopicsDistrEnriched,
  NuclearTopicsDistr,
  CategoryWithVisibility as CategoriesWithVisibility,
  CategoryWithVisibility,
  NUCLEAR_TOPICS_EXPLANATION,
  GraphTopic,
} from '../../models/ai-model';
import {
  BubbleDataPoint,
  Chart,
  ChartTypeRegistry,
  Point,
  registerables,
} from 'chart.js';
import { SharedModule } from '../../shared/shared.module';
import { NuclearTopicsLabelService } from '../../services/label.nucleartopics.service';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-ai-graphics',
  standalone: true,
  templateUrl: './ai-graphics.component.html',
  styleUrls: ['./ai-graphics.component.scss'],
  imports: [
    SharedModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    FormsModule,
  ],
})
export class AiGraphicsComponent implements OnInit, OnChanges {
  private _catDistrPerEachAppointment: NuclearTopicsDistrEnriched[] = [];
  @Input() set catDistrPerEachAppointment(value: NuclearTopicsDistrEnriched[]) {
    this._catDistrPerEachAppointment = value;
  }
  get catDistrPerEachAppointment() {
    return this._catDistrPerEachAppointment;
  }
  @ViewChild('chart') chartElement: ElementRef<HTMLCanvasElement> | undefined;

  appointmentsWithoutCatDistrScores: NuclearTopicsDistrEnriched[] = [];
  buttonText: string = 'Nascondi Appuntamenti senza CatDistr';
  hideNullCatDistr: boolean = false;

  isMobilePortrait: boolean = window.innerWidth < 768;

  displayGraph: boolean = true;

  private chart:
    | Chart<'line', { x: number; y: number | null }[], any>
    | undefined;

  selectedCategories: CategoriesWithVisibility[] = [];
  protected colors: string[] = [
    'rgb(112,128,144)', // Dark gray
    'rgb(245, 223, 179)', // Subdued Pastel Orange
    'rgb(245, 235, 179)', // Subdued Pastel Yellow
    'rgb(179, 235, 185)', // Subdued Pastel Green
    'rgb(179, 220, 235)', // Subdued Pastel Blue
    'rgb(235, 179, 235)', // Subdued Pastel Purple
    'rgb(235, 219, 250)', // Subdued Pink Lavender
    'rgb(214, 163, 163)', // Subdued Light Red
    'rgb(150, 120, 220)', // Subdued Light Purple
    'rgb(100, 180, 220)', // Subdued Light Blue
    'rgb(84, 224, 208)', // Subdued Teal
    'rgb(222, 180, 180)', // Subdued Light Pink
  ];
  categories: CategoriesWithVisibility[] = [
    { name: 'SPS', hidden: false },
    { name: 'FAM', hidden: false },
    { name: 'RA', hidden: false },
    { name: 'OS', hidden: false },
    { name: 'RS', hidden: false },
    { name: 'MDE', hidden: false },
    { name: 'PRG', hidden: false },
    { name: 'ET', hidden: false },
    { name: 'RCT', hidden: false },
    { name: 'SES', hidden: false },
    { name: 'BNS', hidden: false },
    { name: 'SV', hidden: false },
  ];

  nuclearTopicsExplanation: GraphTopic[] = NUCLEAR_TOPICS_EXPLANATION;
  nuclearTopicsLabels: { [key: string]: string } = {};

  private viewInitialized = false;

  constructor(public nuclearTopicsLabelService: NuclearTopicsLabelService) {
    this.nuclearTopicsLabels = this.nuclearTopicsLabelService.getTopicLabels();
    Chart.register(...registerables);
    this.selectedCategories = [...this.categories];
  }
  public getCanvas(): HTMLCanvasElement | undefined {
    return this.chartElement?.nativeElement;
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    // Try to initialize the chart if we already have data
    if (
      this._catDistrPerEachAppointment &&
      this._catDistrPerEachAppointment.length > 0
    ) {
      setTimeout(() => {
        this.initializeChart();
      });
    }
  }

  private initializeChart(): void {
    if (!this.viewInitialized || !this.chartElement) {
      console.warn('View not initialized or chart element not available');
      return;
    }

    let cont = 0;
    this._catDistrPerEachAppointment.forEach((appointment) => {
      if (appointment.categories_distribution_available === true) {
        cont++;
      }
    });

    if (cont > 0) {
      this.displayGraph = true;
      this.updateTopCategories();
      this.updateChart();
    } else {
      this.displayGraph = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catDistrPerEachAppointment'] && this.viewInitialized) {
      this.initializeChart();
    }
  }

  ngOnInit(): void {
    console.log('Categories Distribution data loaded into graph');
  }

  updateTopCategories(): void {
    const categoryTotals: Record<string, number> = {};

    // Initialize totals for all categories
    this.categories.forEach((category) => {
      categoryTotals[category.name] = 0;
    });

    // Sum up the percentages for each category across all appointments
    this._catDistrPerEachAppointment.forEach((appointment) => {
      if (appointment.categories_distribution) {
        Object.entries(appointment.categories_distribution).forEach(
          ([category, value]) => {
            if (category in categoryTotals) {
              categoryTotals[category] += value || 0;
            }
          }
        );
      }
    });

    // Sort categories by total score and get top 3
    const sortedCategories = Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([name]) => name);

    // Update the 'hidden' property for each category
    this.categories.forEach((category) => {
      category.hidden = !sortedCategories.includes(category.name);
    });

    this.selectedCategories = this.categories.filter(
      (category) => !category.hidden
    );
  }

  updateChart(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    this.createChart();
  }

  createChart(): void {
    if (!this.chartElement || !this.chartElement.nativeElement) {
      console.warn('Chart element not available, skipping chart creation');
      return;
    }

    if (
      !this._catDistrPerEachAppointment ||
      this._catDistrPerEachAppointment.length === 0
    ) {
      console.warn('No data available for chart creation');
      return;
    }

    const filteredAppointments = this.hideNullCatDistr
      ? this._catDistrPerEachAppointment.filter(
          (appointment) => appointment.categories_distribution_available
        )
      : this._catDistrPerEachAppointment;

    // Sort appointments by date
    filteredAppointments.sort((a, b) => {
      const dateA = this.parseDate(a.date);
      const dateB = this.parseDate(b.date);
      return dateA.getTime() - dateB.getTime();
    });

    const datasets = this.categories.map((category, index) => {
      const data = filteredAppointments.map(
        (appointment, appointmentIndex) => ({
          x: appointmentIndex,
          y:
            appointment.categories_distribution?.[
              category.name as keyof NuclearTopicsDistr
            ] ?? null,
        })
      );

      return {
        label: this.nuclearTopicsLabels[category.name],
        data: data,
        borderColor: this.colors[index % this.colors.length],
        backgroundColor: this.colors[index % this.colors.length],
        fill: false, // Nessun riempimento sotto le linee
        hidden: category.hidden,
        tension: 0.4, // Linee fluide
      };
    });

    this.chart = new Chart(this.chartElement.nativeElement, {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            title: {
              display: false, // TODO: If I'm not showing this I should delete the two lines below
              text: 'Colloqui',
              font: { size: 14, weight: 'bold' },
            },
            ticks: {
              stepSize: 1,
              callback: (value) => {
                const index = Math.round(value as number);
                return index >= 0 && index < filteredAppointments.length
                  ? this.formatDateItalian(filteredAppointments[index].date)
                  : '';
              },
              font: { size: 14 },
            },
            grid: {
              lineWidth: 0,
            },
          },
          y: {
            min: 0,
            max: 100,
            title: {
              display: true,
              text: '%',
              font: { size: 14, weight: 'bold' },
            },
            ticks: {
              stepSize: 10,
              callback: (value) => (Number.isInteger(value) ? value : ''),
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
        elements: {
          point: {
            radius: (context) => {
              const value = (context.raw as { y: number }).y; // Cast di context.raw
              // Scala la dimensione in base al valore: 5 per 0, 20 per 100
              const minRadius = 5;
              const maxRadius = 10;
              const scaleFactor = (maxRadius - minRadius) / 100;
              return minRadius + value * scaleFactor;
            },
            borderWidth: 1,
            hoverRadius: (context) => {
              const value = (context.raw as { y: number }).y; // Cast di context.raw
              const minHoverRadius = 7;
              const maxHoverRadius = 15;
              const scaleFactor = (maxHoverRadius - minHoverRadius) / 100;
              return minHoverRadius + value * scaleFactor;
            },
          },
          line: {
            borderWidth: 2, // Spessore delle linee
          },
        },
      },
    });
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  private formatDateItalian(dateString: string): string {
    const date = this.parseDate(dateString);
    const currentYear = new Date().getFullYear();
    const day = date.getDate();
    const month = this.getItalianMonthAbbreviation(date.getMonth());
    const year = date.getFullYear();

    if (year === currentYear) {
      return `${day} ${month}`;
    } else {
      return `${day} ${month} ${year.toString().slice(-2)}`;
    }
  }

  private getItalianMonthAbbreviation(monthIndex: number): string {
    const italianMonths = [
      'Gen',
      'Feb',
      'Mar',
      'Apr',
      'Mag',
      'Giu',
      'Lug',
      'Ago',
      'Set',
      'Ott',
      'Nov',
      'Dic',
    ];
    return italianMonths[monthIndex];
  }

  hideShowNullCategories() {
    this.hideNullCatDistr = !this.hideNullCatDistr;
    if (this.hideNullCatDistr) {
      this.buttonText = 'Mostra Appuntamenti senza CatDistr';
    } else {
      this.buttonText = 'Nascondi Appuntamenti senza CatDistr';
    }
    this.updateChart();
  }

  toggleCategory(category: CategoryWithVisibility): void {
    category.hidden = !category.hidden;
    this.updateChart();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobilePortrait = window.innerWidth < 768;
  }

  public getChart(): Chart | undefined {
    return this.chart as
      | Chart<
          keyof ChartTypeRegistry,
          (number | [number, number] | Point | BubbleDataPoint | null)[],
          unknown
        >
      | undefined;
  }

  public getColors(): string[] {
    return this.colors;
  }
}
